import {
  Button,
  Checkbox,
  FlashMessage,
  Modal,
  Radio,
  SelectNew,
} from "../../../components/common";
import React, { useEffect, useState } from "react";
import ArrowUp from "../icons/arrow-up.svg";
import { useFieldArray, useForm } from "react-hook-form";
import LocationPinDrop from "../icons/map-pin-drop.svg";
import PricingIcon from "../icons/pricing-icon.svg";
import PinkPlus from "../icons/plus-pink.svg";
import WhiteCross from "../icons/x-pink.svg";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import commonServices from "../../../services/common.services";
import _ from "lodash";
import { updateCandidateServicesRequest } from "../../../reducers/candidate/candidate.reducer";
import { OptionsProp } from "../../../components/common/SelectNew";
import { pluckFromArray } from "../../../utils/helper";
import { currencyListRequest } from "../../../reducers/currency/currency.reducer";
import EditIcon from "../icons/edit-icon.svg";
import AsyncSelect from "../../common/AsyncSelect";
import LocationIcon from "../icons/location-select.svg";

interface ServicesData {
  services: { name: string }[];
  location: { value: number; label: string } | null;
  isRemote: number;
  pricingOption: number | string;
  currency: { value: number; label: string } | null;
  hourlyRate: { value: number; label: string } | null;
}

interface LocationOption {
  value: number | string;
  label: string;
}

interface Location {
  id: number | string;
  location: string;
}
let searchDelay: NodeJS.Timeout;

function ServicesSection({
  servicesData,
  isPublicPage,
}: {
  servicesData: ServicesData[];
  isPublicPage?: boolean;
}) {
  const dispatch = useDispatch();
  const [currencyOption, setCurrencyOption] = useState<OptionsProp[]>([]);
  const [servicesModal, setServicesModal] = useState(false);
  const [selectServiceModal, setSelectServiceModal] = useState(false);
  const [specificServicesModal, setSpecificServicesModal] =
    useState<boolean>(false);
  const [localData, setLocalData] = useState<ServicesData[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState<any>([]);

  const { candidateViewFlag, message: candidateMessage } = useSelector(
    ({ candidate }: rootReducersState) => ({
      candidateViewFlag: candidate.candidates.candidateViewFlag,
      message: candidate.candidates.message,
    })
  );

  const {
    currencies: { data: currencyList },
  } = useSelector(({ currency }: rootReducersState) => currency);
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<{ servicesData: ServicesData[] }>({
    defaultValues: { servicesData: servicesData || [] },
  });

  const { fields, append } = useFieldArray({
    control,
    name: "servicesData",
  });
  const [selectedServices, setSelectedServices] = useState<{
    [key: number]: { name: string; label?: string }[];
  }>({});

  // Helper to get selected services for a given index
  const getSelectedServices = (index: number) => selectedServices[index] || [];

  useEffect(() => {
    if (fields.length > 0) {
      const initial: {
        [key: number]: { name: string; label?: string }[];
      } = {};
      fields.forEach((field, idx) => {
        initial[idx] =
          servicesData[idx]?.services?.map((s: any) =>
            typeof s?.name === "string" ? { name: s.name, label: s.name } : s
          ) || [];
      });
      setSelectedServices(initial);
    }
    // eslint-disable-next-line
  }, [fields.length, servicesData]);

  const openServicesModal = () => {
    if (isPublicPage) return;
    const updatedProjects = watch("servicesData");
    reset({ servicesData: updatedProjects });
    setServicesModal(true);
  };

  const closeSocialLinkModal = () => {
    const updatedProjects = watch("servicesData");
    reset({ servicesData: updatedProjects });
    setServicesModal(false);
  };

  // Map incoming data to form structure with proper type conversion
  useEffect(() => {
    if (!_.isEmpty(servicesData)) {
      const mappedServicesData = servicesData.map((service) => ({
        ...service,
        // Convert pricingOption from number to string for Radio component
        pricingOption: String(service.pricingOption || "0"),
        // Ensure other fields are properly formatted
        currency:
          !service.currency ||
          (typeof service.currency === "object" && !service.currency.label)
            ? null
            : service.currency,
        // Normalize hourlyRate: set to null if empty to show placeholder
        hourlyRate: 
          !service.hourlyRate || 
          (typeof service.hourlyRate === 'object' && !service.hourlyRate.value && !service.hourlyRate.label)
            ? null 
            : service.hourlyRate,
        // Normalize location: set to null if empty to show placeholder
        location: 
          !service.location || 
          (typeof service.location === 'object' && !service.location.value && !service.location.label)
            ? null 
            : service.location,
        isRemote: Number(service.isRemote) || 0,
        services: service.services || [],
      }));

      reset({ servicesData: mappedServicesData });
      setLocalData(servicesData);
    }
    // eslint-disable-next-line
  }, [servicesData, reset]);

  const onSubmit = (data: { servicesData: ServicesData[] }) => {
    const payload = data.servicesData.map((service, idx) => ({
      ...service,
      services: getSelectedServices(0)?.map((item: any) => ({
        name: item.label,
      })),
      pricingOption: Number(service.pricingOption),
      hourlyRate: service.hourlyRate?.value,
      location: service.location?.value,
      currency: service.currency?.label,
    }));

    dispatch(updateCandidateServicesRequest(payload[0]));
  };

  useEffect(() => {
    _.isEmpty(currencyList) && dispatch(currencyListRequest());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!_.isEmpty(currencyList)) {
      const setCurrencyOptions = pluckFromArray(
        currencyList,
        "id",
        "code",
        "value",
        "label"
      ) as OptionsProp[];
      setCurrencyOption(setCurrencyOptions);
    }
    // eslint-disable-next-line
  }, [currencyList]);

  useEffect(() => {
    if (candidateViewFlag === "services_success") {
      FlashMessage("Services updated successfully");
      const updatedData = watch("servicesData");
      const updatedWithServices = updatedData.map((item) => ({
        ...item,
        services:
          getSelectedServices(0)?.map((serviceItem) => ({
            name: serviceItem.label || "",
          })) || [],
      }));

      setLocalData(updatedWithServices);
      reset({ servicesData: updatedData });
      setServicesModal(false);
    }
    if (candidateViewFlag === "services_failed") {
      FlashMessage(
        candidateMessage || "Failed to update services",
        "error"
      );
    }

    // eslint-disable-next-line
  }, [candidateViewFlag, candidateMessage]);

  const searchLocations = (search: string) =>
    new Promise((resolve) => {
      clearTimeout(searchDelay);
      searchDelay = setTimeout(async () => {
        const locationData = await commonServices.fetchLocations(search);

        const locationDataTyped: { data?: { data?: Location[] } } =
          locationData;
        const locations: LocationOption[] = _.get(
          locationDataTyped,
          "data.data",
          []
        ).map((c: Location) => ({
          value: c.id,
          label: c.location,
        }));

        resolve(locations);
      }, 1000);
    });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    if (value !== "") {
      clearTimeout(searchDelay);
      searchDelay = setTimeout(async () => {
        const jobTitleDetails = await commonServices.fetchJobTitles(value);
        const data = _.get(jobTitleDetails, "data", []) as {
          id: number;
          title: string;
        }[];
        const locations = data.map((c) => ({
          value: c.id,
          label: c.title,
        }));
        setSearchResults(locations);
      }, 500);
    } else {
      setSearchResults([]);
    }
  };

  const closeServicesModal = () => {
    setSelectServiceModal(false);
    setSearchText("");
  };

  const handleServiceSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // No need to validate main form here
    setValue("servicesData.services" as any, selectedServices);
    closeServicesModal();
  };

  return (
    <div className="accordion">
      <div className="accordion-head">
        <b>Services</b>
        {!isPublicPage &&
          (!_.isEmpty(localData) ? (
            <div className="edit-icon" onClick={openServicesModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openServicesModal}>+Add</span>
          ))}
      </div>
      {!_.isEmpty(localData) && (
        <div className="accordion-body">
          <div className="services-content">
            <div className="service-tag-wrapper">
              {localData?.map((service) =>
                service?.services?.map((item, index) => (
                  <span key={index} className="service-tag">
                    {item?.name}
                  </span>
                ))
              )}
            </div>
            <Button
              className="all-services-btn"
              onClick={() => setSpecificServicesModal(true)}
            >
              <span>All Services</span>
              <img src={ArrowUp} alt="Arrow" />
            </Button>
          </div>
        </div>
      )}

      {!isPublicPage && (
        <Modal
          visible={servicesModal}
          onClose={closeSocialLinkModal}
          className={`candidate-profile-modal services-modal`}
          size="large"
          title="Let’s get your services up and running"
        >
        <div className="modal-body-wrapper">
          <form onSubmit={handleSubmit(onSubmit)}>
            {!_.isEmpty(fields) ? (
              fields?.map((item, index) => (
                <div className="services-modal-wrapper" key={index}>
                  <div
                    className={`add-service ${
                      getSelectedServices(index)?.length > 0 ? "mb-0" : ""
                    }`}
                    onClick={() => {
                      setSelectServiceModal(true);
                    }}
                  >
                    <div className="add-service-icon">
                      <img src={PinkPlus} alt="edit" />
                    </div>
                    <span>Add Service</span>
                  </div>
                  {getSelectedServices(index) &&
                  getSelectedServices(index).length > 0 &&
                  selectServiceModal === false ? (
                    <div className="selected-service-wrapper">
                      {getSelectedServices(index).map((item, key) => (
                        <div key={key}>
                          <span>{item?.label}</span>
                          <div
                            className="icon"
                            onClick={() =>
                              setSelectedServices((prev) => {
                                const updated = { ...prev };
                                updated[index] = (updated[index] || []).filter(
                                  (service: { name: string; label?: string }) =>
                                    service.label !== item.label
                                );
                                return updated;
                              })
                            }
                          >
                            <img src={WhiteCross} alt="cross" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    ""
                  )}

                  <div className="service-modal-content">
                    <div className="left-col">
                      <div className="work-choice">
                        <label htmlFor="service_name" className="form-label">
                          Where would you like to work?
                        </label>
                      </div>
                      <div className="form-group">
                        <AsyncSelect
                          name={`servicesData.${index}.location`}
                          placeholder="Select your Work Location"
                          externalLabel={{
                            label: "Work Location",
                            className: "form-label",
                          }}
                          icon={<img src={LocationIcon} alt="" />}
                          onStartSearching={searchLocations}
                          noOptionsMessage="No matching job found"
                          validationObj={errors}
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: "Select your Work Location",
                            },
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <Checkbox
                          control={control}
                          inputRef={register(`servicesData.${index}.isRemote`)}
                          externalLabel={{
                            label: "Open to Remote Work",
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setValue(
                              `servicesData.${index}.isRemote`,
                              e.target.checked ? 1 : 0
                            );
                          }}
                          defaultChecked={
                            Number(item?.isRemote) === 1 ? true : false
                          }
                        />
                      </div>
                    </div>
                    <div className="arrow"></div>
                    <div className="col-right">
                      <div className="work-choice">
                        <label htmlFor="service_name" className="form-label">
                        Pricing Options
                        </label>
                      </div>
                      <div className="pricing-modal">
                        <div className="form-group custom-radio">
                          <Radio
                            control={control}
                            labelPlacement="end"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setValue(
                                `servicesData.${index}.pricingOption`,
                                String(e.target.value)
                              );
                            }}
                            options={[
                              {
                                label: "Select your pricing model",
                                value: "1",
                              },
                              { label: "Contact for pricing", value: "0" },
                            ]}
                            inputRef={register(
                              `servicesData.${index}.pricingOption`,
                              {
                                required: {
                                  value: true,
                                  message: "Please select pricing option",
                                },
                              }
                            )}
                            name={`servicesData[${index}].pricingOption`}
                          />
                        </div>
                        {Number(
                          watch(`servicesData.${index}.pricingOption`)
                        ) === 1 && (
                          <div className="currency-wrapper">
                            <div className="form-group">
                              <SelectNew
                                placeholder="USD"
                                options={currencyOption}
                                validationObj={errors}
                                control={control}
                                externalLabel={{
                                  label: "Currency",
                                }}
                                rules={{
                                  required: {
                                    value:
                                      watch(
                                        `servicesData.${index}.pricingOption`
                                      ) === "1",
                                    message: "Currency required",
                                  },
                                }}
                                name={`servicesData[${index}].currency`}
                              />
                            </div>
                            <div className="form-group">
                              <SelectNew
                                name={`servicesData[${index}].hourlyRate`}
                                placeholder="Pricing"
                                externalLabel={{
                                  label: "Pricing per hour",
                                }}
                                options={[
                                  {
                                    value: "5",
                                    label: `$5`,
                                  },
                                  { value: "10", label: `$10` },
                                  {
                                    value: "15",
                                    label: `$15`,
                                  },
                                  {
                                    value: "20",
                                    label: `$20`,
                                  },
                                  { value: "25", label: `$25` },
                                  {
                                    value: "30",
                                    label: `$30`,
                                  },
                                ]}
                                validationObj={errors}
                                control={control}
                                rules={{
                                  required: {
                                    value:
                                      watch(
                                        `servicesData.${index}.pricingOption`
                                      ) === "1",
                                    message: "Hourly rate required",
                                  },
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <Button
                type="button"
                className="add-new-btn"
                onClick={() =>
                  append({
                    services: [],
                    location: null,
                    isRemote: 0,
                    pricingOption: "",
                    currency: null,
                    hourlyRate: null,
                  })
                }
              >
                Add new
              </Button>
            )}
            <div className="btn-wrapper">
              <Button
                variant="outlined"
                className="outlined"
                color="none"
                onClick={closeSocialLinkModal}
              >
                Cancel
              </Button>
              <Button className="save-btn" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      )}
      <Modal
        className="candidate-profile-modal view-service-modal"
        isOpen={specificServicesModal}
        onClose={() => setSpecificServicesModal(false)}
        title="Location-Specific Service with Pricing Details"
        visible={specificServicesModal}
        size="large"
      >
        <div className="modal-body-wrapper">
          <div className="services-modal-wrapper all-services">
            <div className="about-card">
              <h5>About</h5>
              <div className="about-card-content-wrapper">
                <div className="about-card-content">
                  <div className="icon">
                    <img src={LocationPinDrop} alt="icon" />
                  </div>
                  <div>
                    <b>Location</b>
                    <span>{localData?.[0]?.location?.label}</span>
                  </div>
                </div>
                <div className="about-card-content">
                  <div className="icon">
                    <img src={PricingIcon} alt="icon" />
                  </div>
                  <div>
                    <b>Pricing</b>
                    {Number(localData?.[0]?.pricingOption) === 1 ? (
                      <span>
                        Hourly rates starting from{" "}
                        {localData?.[0]?.hourlyRate?.label}/
                      </span>
                    ) : (
                      <span>Contact for pricing</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="about-card">
              <h5>Services</h5>
              <div className="selected-service-wrapper">
                {localData?.[0]?.services?.map((item, index) => (
                  <div key={index}>{item?.name}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        className="candidate-profile-modal"
        isOpen={selectServiceModal}
        onClose={closeServicesModal}
        title="Select Your Services"
        visible={selectServiceModal}
      >
        <div className="modal-body-wrapper">
          <form
            onSubmit={handleServiceSubmit}
            className="services-modal-wrapper service-modal"
          >
            <div className="add-service">
              <div className="add-service-icon">
                <img src={PinkPlus} alt="edit" />
              </div>
              <input
                type="text"
                placeholder="Search Service"
                className="form-control"
                value={searchText}
                onChange={handleSearch}
              />
            </div>
            {Object.entries(selectedServices).map(([idx, services]) =>
              services.length > 0 ? (
                <div className="selected-service-wrapper" key={idx}>
                  {services.map(
                    (item: { name: string; label?: string }, index: number) => (
                      <div key={index}>
                        <span>{item?.label}</span>
                        <div
                          className="icon"
                          onClick={() =>
                            setSelectedServices((prev: any) => {
                              const updated = { ...prev };
                              updated[idx] = (updated[idx] || []).filter(
                                (service: { name: string; label?: string }) =>
                                  service.label !== item.label
                              );
                              return updated;
                            })
                          }
                        >
                          <img src={WhiteCross} alt="cross" />
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : null
            )}
            {searchResults.length > 0 && (
              <div className="service-dropdown-wrapper">
                {searchResults.map((item: any, idx: number) => {
                  // Check if item is selected in selectedServices[0] by name
                  const isSelected = getSelectedServices(0).some(
                    (s) => s.label === item.label
                  );
                  return (
                    <div
                      className={`drop-down ${isSelected ? " active" : ""}`}
                      key={idx}
                      onClick={() => {
                        setSelectedServices((prev) => {
                          const updated = { ...prev };
                          if (isSelected) {
                            // Remove item if already selected
                            updated[0] = (updated[0] || []).filter(
                              (s) => s.label !== item.label
                            );
                          } else {
                            // Add item if not selected
                            updated[0] = [...(updated[0] || []), item];
                          }
                          return updated;
                        });
                      }}
                    >
                      {item.label}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="btn-wrapper">
              <Button
                variant="outlined"
                onClick={closeServicesModal}
                className="outlined"
                color="none"
              >
                Cancel
              </Button>
              <Button className="save-btn" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </div>
        </Modal>
      
    </div>
  );
}

export default ServicesSection;
